import { useContext } from 'react'
import { useTranslation } from 'react-i18next'

import { Alert, Divider, LineItem, Text, ToastContext } from '@reveni-io/reveni-ui'

import SaveChangesButtons from '@/common/components/SaveChangesButtons/SaveChangesButtons'
import UnsavedChangesAlert from '@/common/components/UnsavedChangesAlert/UnsavedChangesAlert'
import { userPermissions } from '@/common/constants'
import AppContext from '@/common/context/app.context'
import useUserPermissions from '@/common/hooks/useUserPermissions/useUserPermissions'

import DigitalProductForm from './DigitalProductForm/DigitalProductForm'
import DigitalProductPreview from './DigitalProductPreview/DigitalProductPreview'
import DigitalProductTabSkeleton from './DigitalProductTabSkeleton/DigitalProductTabSkeleton'
import useReturnProtectionProduct from './hooks/useReturnProtectionProduct/useReturnProtectionProduct'
import { getReturnProtectionLineItem } from './helpers'
import * as Styles from './styles'

const DigitalProductTab = () => {
  const { t } = useTranslation()
  const { info } = useContext(AppContext)
  const { setToastInfo } = useContext(ToastContext)
  const { hasPermission } = useUserPermissions()

  const canEdit = hasPermission(userPermissions.editReturnProtection)

  const {
    product,
    productError,
    isBusy,
    savingProduct,
    resettingProduct,
    title,
    image,
    hasChanges,
    isTitleEmpty,
    uploaderKey,
    changeTitle,
    changeImage,
    revertChanges,
    onSave,
    onResetToDefault,
  } = useReturnProtectionProduct({ storeId: info.selectedStore.id, setToastInfo })

  if (productError) {
    return <Alert type="error">{t('No se ha podido cargar el producto digital.')}</Alert>
  }

  if (!product) {
    return <DigitalProductTabSkeleton />
  }

  return (
    <Styles.Container>
      {hasChanges && <UnsavedChangesAlert hasPermission={canEdit} />}
      <Styles.Header>
        <Text type="titleM">{t('Producto digital')}</Text>
        <Text type="bodyMRegular">
          {t('Personaliza cómo se ve el producto digital de Return Protection en tu tienda.')}
        </Text>
      </Styles.Header>
      <Styles.Content>
        <DigitalProductForm
          title={title}
          onChangeTitle={changeTitle}
          onChangeImage={changeImage}
          uploaderKey={uploaderKey}
          onResetToDefault={onResetToDefault}
          resetting={resettingProduct}
          resetDisabled={!canEdit || isBusy}
        />
        <Styles.ProductCard>
          <LineItem item={getReturnProtectionLineItem({ title, image })} />
        </Styles.ProductCard>
      </Styles.Content>
      <Divider />
      <DigitalProductPreview title={title} image={image} />
      <SaveChangesButtons
        hasChanges={canEdit && hasChanges}
        onSave={onSave}
        onRevertChanges={revertChanges}
        loading={savingProduct}
        disabled={isBusy || isTitleEmpty}
      />
    </Styles.Container>
  )
}

export default DigitalProductTab
